import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../config/api';

const GlobalSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const searchRef = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }

      if (e.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Search debounce
  useEffect(() => {
    if (searchTerm.length < 2) {
      setResults([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const response = await axios.get(
          `${API_URL}/orders/search?q=${encodeURIComponent(searchTerm)}`
        );
        setResults(response.data.results || []);
        setSelectedIndex(0);
      } catch (error) {
        console.error('Search error:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev =>
        prev < results.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault();
      handleSelectResult(results[selectedIndex]);
    }
  };

  const handleSelectResult = (result) => {
    navigate(`/order/${result.netsuite_id}`);
    setIsOpen(false);
    setSearchTerm('');
    setResults([]);
  };

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="global-search" ref={searchRef}>
      <div className="global-search__input-wrapper">
        <i className="bi bi-search global-search__icon"></i>
        <input
          ref={inputRef}
          type="text"
          className="global-search__input"
          placeholder="Search orders... (⌘K)"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
        />
        {searchTerm && (
          <button
            className="global-search__clear"
            onClick={() => {
              setSearchTerm('');
              setResults([]);
            }}
          >
            <i className="bi bi-x"></i>
          </button>
        )}
      </div>

      {isOpen && (searchTerm.length >= 2 || results.length > 0) && (
        <div className="global-search__dropdown">
          {loading ? (
            <div className="global-search__loading">
              <div className="global-search__spinner"></div>
              <span>Searching...</span>
            </div>
          ) : results.length === 0 && searchTerm.length >= 2 ? (
            <div className="global-search__empty">
              <i className="bi bi-search"></i>
              <span>No results found for "{searchTerm}"</span>
            </div>
          ) : (
            <div className="global-search__results">
              <div className="global-search__results-header">
                <span>Sales Orders</span>
                <span className="global-search__results-count">
                  {results.length} results
                </span>
              </div>
              {results.map((result, index) => (
                <button
                  key={result.netsuite_id}
                  className={`global-search__result ${
                    index === selectedIndex ? 'global-search__result--selected' : ''
                  }`}
                  onClick={() => handleSelectResult(result)}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <div className="global-search__result-icon">
                    <i className="bi bi-file-text"></i>
                  </div>
                  <div className="global-search__result-content">
                    <div className="global-search__result-title">
                      {result.salesOrderNo}
                    </div>
                    <div className="global-search__result-subtitle">
                      {result.customerCode} • {result.customerName}
                    </div>
                  </div>
                  <i className="bi bi-arrow-return-left global-search__result-hint"></i>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
